import { replaceIdMiddleware } from '@blocksuite/affine/shared/adapters';
import type { DocSnapshot } from '@blocksuite/affine/store';
import { Transformer } from '@blocksuite/affine/store';
import { Service } from '@toeverything/infra';

import type { WorkspaceProjectData } from '../../../utils/first-app-data';
import type { DocsService } from '../../doc';
import { OrganizeService } from '../../organize';
import {
  getAFFiNEWorkspaceSchema,
  type WorkspaceMetadata,
  type WorkspacesService,
} from '../../workspace';

export class FolderTemplateService extends Service {
  constructor(
    private readonly docsService: DocsService,
    private readonly organizeService: OrganizeService,
    private readonly workspacesService: WorkspacesService
  ) {
    super();
  }

  /**
   * Instantiate a folder template inside the current workspace: duplicate
   * every doc contained in the source folder subtree (rewriting cross-doc
   * references to the new copies) and recreate the folder structure under
   * `targetParentId` (root when omitted).
   *
   * @param sourceFolderId - the folder marked as a template
   * @param targetParentId - the parent folder to place the copy in (root when null)
   * @returns the id of the new root folder copy
   */
  async instantiate(
    sourceFolderId: string,
    targetParentId: string | null = null
  ): Promise<string> {
    // 1. collect all doc ids inside the source subtree
    const sourceDocIds = this.organizeService.collectDocIds(sourceFolderId);

    // 2. duplicate doc contents in a single session so refs between the docs
    //    resolve to the new copies
    const docIdMap = await this.docsService.duplicateDocsBatch(sourceDocIds);

    // 3. recreate the folder structure, remapping doc links
    return this.organizeService.duplicateSubtree(
      sourceFolderId,
      targetParentId,
      docIdMap
    );
  }

  /**
   * Create a brand new workspace seeded from a folder template: copies the
   * folder's docs (with blobs and cross-doc references) and recreates the
   * folder structure inside the new workspace.
   *
   * @param sourceFolderId - the folder marked as a template
   * @param workspaceName - name for the new workspace
   * @param flavour - workspace flavour (defaults to `local`)
   * @param projectData - optional project fields (client, deadline, ...)
   * @returns metadata of the created workspace
   */
  async instantiateAsWorkspace(
    sourceFolderId: string,
    workspaceName: string,
    flavour = 'local',
    projectData?: WorkspaceProjectData
  ): Promise<WorkspaceMetadata> {
    const schema = getAFFiNEWorkspaceSchema();

    // --- read structure + doc snapshots from the source workspace ---
    const structure = this.organizeService.exportSubtree(sourceFolderId);
    const sourceDocIds = this.organizeService.collectDocIds(sourceFolderId);
    const sourceCollection = this.docsService.getBlocksuiteCollection();

    const exportJob = new Transformer({
      schema,
      blobCRUD: sourceCollection.blobSync,
      docCRUD: {
        create: (id: string) => sourceCollection.createDoc(id).getStore({ id }),
        get: (id: string) =>
          sourceCollection.getDoc(id)?.getStore({ id }) ?? null,
        delete: (id: string) => sourceCollection.removeDoc(id),
      },
      middlewares: [],
    });

    const snapshots: DocSnapshot[] = [];
    const blobIds = new Set<string>();
    for (const docId of sourceDocIds) {
      const store = sourceCollection.getDoc(docId)?.getStore({ id: docId });
      if (!store) continue;
      const snapshot = exportJob.docToSnapshot(store);
      if (!snapshot) continue;
      snapshots.push(snapshot);
      // collect referenced blobs (images / attachments)
      exportJob.walk(snapshot, block => {
        const sourceId = block.props?.sourceId as string | undefined;
        if (sourceId) {
          blobIds.add(sourceId.replace(/^\//, ''));
        }
      });
    }

    // pre-generate old -> new doc ids so cross-doc refs resolve on import
    const docIdMap = new Map<string, string>();
    for (const snapshot of snapshots) {
      docIdMap.set(snapshot.meta.id, sourceCollection.idGenerator());
    }

    // --- create the new workspace and seed it ---
    const metadata = await this.workspacesService.create(
      flavour,
      async docCollection => {
        docCollection.meta.initialize();
        const metaMap = docCollection.doc.getMap('meta');
        metaMap.set('name', workspaceName);

        // persist optional project data alongside the workspace metadata
        if (projectData) {
          const { description, client, deadline, tags } = projectData;
          if (description) metaMap.set('description', description);
          if (client) metaMap.set('client', client);
          if (deadline) metaMap.set('deadline', deadline);
          if (tags) {
            const normalizedTags = Array.isArray(tags)
              ? tags
              : tags
                  .split(',')
                  .map(tag => tag.trim())
                  .filter(Boolean);
            if (normalizedTags.length > 0) metaMap.set('tags', normalizedTags);
          }
        }

        // copy referenced blobs (blob ids are content hashes, stable across ws)
        for (const blobId of blobIds) {
          try {
            const blob = await sourceCollection.blobSync.get(blobId);
            if (blob) {
              await docCollection.blobSync.set(blobId, blob);
            }
          } catch {
            // ignore missing blobs, keep going
          }
        }

        const importJob = new Transformer({
          schema,
          blobCRUD: docCollection.blobSync,
          docCRUD: {
            create: (id: string) =>
              docCollection.createDoc(id).getStore({ id }),
            get: (id: string) =>
              docCollection.getDoc(id)?.getStore({ id }) ?? null,
            delete: (id: string) => docCollection.removeDoc(id),
          },
          middlewares: [
            replaceIdMiddleware(docCollection.idGenerator, docIdMap),
          ],
        });

        for (const snapshot of snapshots) {
          await importJob.snapshotToDoc(snapshot);
        }
      }
    );

    // --- recreate the folder structure inside the new workspace ---
    const { workspace, dispose } = this.workspacesService.open({ metadata });
    try {
      await workspace.engine.doc.waitForDocReady(workspace.id);
      const targetOrganize = workspace.scope.get(OrganizeService);
      targetOrganize.importSubtree(structure, null, docIdMap);
    } finally {
      dispose();
    }

    return metadata;
  }
}
